/**
 * Blog language metadata — everything about the blog that is NOT the posts themselves.
 *
 * Split out of blog-posts.ts for the same reason site-url.ts was: route files need
 * `isBlogLang` in `beforeLoad` and the index copy in `head`, and neither of those is
 * code-split by TanStack Router (only `component` is). One `import { isBlogLang }`
 * therefore pulled the entire 967 KB post corpus into the chunk every page preloads,
 * landing page included. Keep this module free of post bodies.
 */
import type { BlogLang } from "@/lib/blog-types";

export type { BlogLang };

export const SUPPORTED_LANGS: BlogLang[] = ["en", "es", "de", "fr", "it", "pt", "ru", "zh"];

export function isBlogLang(x: string): x is BlogLang {
  return (SUPPORTED_LANGS as string[]).includes(x);
}

export const LANG_LABELS: Record<BlogLang, string> = {
  en: "English",
  es: "Español",
  de: "Deutsch",
  fr: "Français",
  it: "Italiano",
  pt: "Português",
  ru: "Русский",
  zh: "中文",
};

export const BLOG_INDEX_META: Record<
  BlogLang,
  {
    title: string;
    description: string;
    heading: string;
    sub: string;
    readCta: string;
    keywords: string;
  }
> = {
  en: {
    title: "SmsEmpire Blog — SMS verification, disposable email & trusted accounts",
    description:
      "Tutorials and guides for anonymous SMS verification, disposable email and buying trusted social media accounts with real followers.",
    heading: "SMS verification, privacy & trusted account guides",
    sub: "Practical tutorials to create anonymous WhatsApp, Telegram and buy Instagram accounts for your business.",
    readCta: "Read guide",
    keywords:
      "sms verification guide, receive sms online, virtual number tutorial, buy instagram accounts, aged accounts, disposable email tutorial, whatsapp without number, telegram without number",
  },
  es: {
    title: "Blog SmsEmpire — Verificación SMS, email desechable y cuentas fiables",
    description:
      "Tutoriales y guías para verificación SMS anónima, email desechable y compra de cuentas de redes sociales con seguidores reales.",
    heading: "Guías de verificación SMS, privacidad y cuentas fiables",
    sub: "Tutoriales prácticos para crear WhatsApp y Telegram anónimos y comprar cuentas de Instagram para tu negocio.",
    readCta: "Leer guía",
    keywords:
      "guia verificacion sms, recibir sms online, tutorial numero virtual, comprar cuentas instagram, cuentas envejecidas, email desechable, whatsapp sin numero, telegram sin numero",
  },
  de: {
    title: "SmsEmpire-Blog – SMS-Verifizierung, Wegwerf-E-Mail und vertrauenswürdige Konten",
    description:
      "Tutorials und Anleitungen zur anonymen SMS-Verifizierung, zur Wegwerf-E-Mail und zum Kauf vertrauenswürdiger Social-Media-Konten mit echten Followern.",
    heading: "Leitfäden für SMS-Verifizierung, Datenschutz und vertrauenswürdige Konten",
    sub: "Praktische Tutorials zum Erstellen anonymer WhatsApp- und Telegram-Konten und zum Kaufen von Instagram-Konten für Ihr Unternehmen.",
    readCta: "Leitfaden lesen",
    keywords:
      "Leitfaden zur SMS-Verifizierung, SMS online empfangen, Tutorial zu virtuellen Nummern, Instagram-Konten kaufen, alte Konten, Tutorial zur Wegwerf-E-Mail, WhatsApp ohne Nummer, Telegram ohne Nummer",
  },
  fr: {
    title: "SmsEmpire Blog — Vérification SMS, email jetable & comptes de confiance",
    description:
      "Tutoriels et guides pour la vérification SMS anonyme, l'email jetable et l'achat de comptes de réseaux sociaux de confiance avec de vrais abonnés.",
    heading: "Vérification SMS, confidentialité & guides de comptes de confiance",
    sub: "Tutoriels pratiques pour créer un compte WhatsApp ou Telegram anonyme et acheter des comptes Instagram pour votre entreprise.",
    readCta: "Lire le guide",
    keywords:
      "guide de vérification sms, recevoir sms en ligne, tutoriel numéro virtuel, acheter comptes instagram, comptes anciens, tutoriel email jetable, whatsapp sans numéro, telegram sans numéro",
  },
  it: {
    title: "SmsEmpire Blog — Verifica SMS, email temporanee e account verificati",
    description:
      "Tutorial e guide per la verifica SMS anonima, email temporanee e acquisto di account social affidabili con follower reali.",
    heading: "Verifica SMS, privacy e guide per account affidabili",
    sub: "Tutorial pratici per creare account WhatsApp e Telegram anonimi e acquistare account Instagram per il tuo business.",
    readCta: "Leggi la guida",
    keywords:
      "guida verifica sms, ricevere sms online, tutorial numero virtuale, comprare account instagram, account storici, tutorial email temporanea, whatsapp senza numero, telegram senza numero",
  },
  pt: {
    title: "SmsEmpire Blog — Verificação por SMS, e-mail descartável e contas confiáveis",
    description:
      "Tutoriais e guias para verificação anônima por SMS, e-mail descartável e compra de contas de mídia social confiáveis com seguidores reais.",
    heading: "Guias de verificação por SMS, privacidade e contas confiáveis",
    sub: "Tutoriais práticos para criar contas anônimas no WhatsApp, Telegram e comprar contas no Instagram para o seu negócio.",
    readCta: "Ler o guia",
    keywords:
      "guia de verificacao por sms, receber sms online, tutorial de numero virtual, comprar contas do instagram, contas antigas, tutorial de email descartavel, whatsapp sem numero, telegram sem numero",
  },
  ru: {
    title: "SmsEmpire Blog — SMS-верификация, одноразовая электронная почта и надежные аккаунты",
    description:
      "Руководства и инструкции по анонимной SMS-верификации, одноразовой электронной почте и покупке надежных аккаунтов в социальных сетях с реальными подписчиками.",
    heading: "Руководства по SMS-верификации, конфиденциальности и надежным аккаунтам",
    sub: "Практические руководства по созданию анонимных аккаунтов WhatsApp, Telegram и покупке аккаунтов Instagram для вашего бизнеса.",
    readCta: "Читать руководство",
    keywords:
      "руководство по sms-верификации, получить sms онлайн, руководство по виртуальному номеру, купить аккаунты instagram, старые аккаунты, руководство по одноразовой электронной почте, whatsapp без номера, telegram без номера",
  },
  zh: {
    title: "SmsEmpire 博客 — 短信验证、临时邮箱及可信账号",
    description: "关于匿名短信验证、临时邮箱以及购买拥有真实粉丝的可信社交媒体账号的教程和指南。",
    heading: "短信验证、隐私与可信账号指南",
    sub: "创建匿名WhatsApp、Telegram以及为您的业务购买Instagram账号的实用教程。",
    readCta: "阅读指南",
    keywords:
      "短信验证指南, 在线接收短信, 虚拟号码教程, 购买instagram账号, 老账号, 临时邮箱教程, 无号码whatsapp, 无号码telegram",
  },
};
